<script setup>
import { ref } from 'vue'
import { UserGroupIcon, AtSymbolIcon } from "@heroicons/vue/outline"
import Alert from '@/Shared/VisualComponents/Alert.vue'
import InputHelpText from '@/Shared/VisualComponents/InputHelpText.vue'
import SetUserOrganisationPermissions from '@/Components/Models/User/Roles/SetUserOrganisationPermissions.vue'

const props = defineProps({
	user: {
		type: Object,
		required: true,
	},
    errors: {
        type: Object,
        default: () => ({}),
    },
	organisation: {
		type: Object,
		default: null
	},
	roles: {
		type: Array,
		required: true,
	},
})

const localUser = ref(props.user)
const selectedOrganisation = ref(props.organisation)

const emit = defineEmits([
	'store:users',
	'set-permissions'
])

function save() {
    emit('create:users', localUser)
}

function changeOrganisation(organisation) {
	selectedOrganisation.value = organisation
}

function setPermissions(permissions) {
	emit('set-permissions', permissions)
}
</script>

<template>
	<SectionCard :title="!!organisation ? 'Creating a new Contact' : 'Creating a new User'">
		<template #default>
			<form @submit.prevent.stop="save" class="w-full">
				<div class="grid grid-cols-1 md:grid-cols-2 gap-x-4">
					<div>
						<Input 
							placeholder="eg. sharonsmith@example.com" 
							v-model="localUser.email" 
							:errors="errors.email" 
							:autofocus="true" 
							label="Email"
							testid="email"
							help-text="This email address will be used to log in."
							type="email"
						/>
					</div>
					<div>
						<Input 
							placeholder="eg. Sharon Smith" 
							v-model="localUser.name" 
							:errors="errors.name" 
							label="Name"
							testid="name"
						/>
					</div>
					<div>
						<Input 
							placeholder="eg. 0404 040 404" 
							v-model="localUser.primary_phone_number" 
							:errors="errors.primary_phone_number" 
							label="Primary Phone Number"
							testid="primary_phone_number"
						/>
					</div>
				</div>

				<div v-if="organisation">
					<Label>Permissions</Label>
					<InputHelpText :help-text="`Select a role for this user within the ${organisation.name} organisation.`" />
					<SetUserOrganisationPermissions
						:user="localUser"
						:roles="roles"
						:organisation="selectedOrganisation"
						@change-organisation="changeOrganisation"
						@set-permissions="setPermissions"
						:errors="errors.permissions ?? null"
					/>
				</div>

				<Alert type="warning" data-testid="creating-admin-user-alert" v-if="user.user_type === 'admin'">
					<div class="flex flex-row items-center w-full h-full text-sm">
						<UserGroupIcon class="h-6 w-6 mr-2" /> You are creating an admin user. This user will have full access to the system with the same capabilities as you do.
					</div>
				</Alert>
			
				<div class="flex items-center">
					<input 
						type="checkbox"
						v-model="localUser.notify"
						data-testid="notify"
						id="notify_new_user"
						class="mr-2 px-2 py-2 mb-1
							text-base font-light
							border rounded
							align-top 
							focus:border-Blue focus:ring-blue-800 text-blue-800
						"
					/>
					<Label target="notify_new_user">
						Notify the user about their new account
						<p class="italic text-xs" v-if="!localUser.notify">You can send the user a 'reset password' email at any time via the edit user page.</p>
					</Label>
				</div>

				<Alert v-if="localUser.notify">
					<div class="flex flex-row items-center w-full h-full text-sm">
						<AtSymbolIcon class="h-6 w-6 mr-2" /> This user will receive an email with a link to verify their account.
					</div>	
				</Alert>
				<button class="hidden" />
			</form>
		</template>
		
		<template #footer>
			<Button
				data-testid="save-btn"
				color="blue"
				@click="save"
			>
				Save
			</Button>
		</template>
	</SectionCard>
</template>